import { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { api, errText } from "../lib/api";
import { PROVINCES } from "../lib/constants";
import { useLanguage } from "../context/LanguageContext";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const RADII = [0, 5, 10, 25, 50, 100, 250];

export function LfAlertPrefs() {
  const { t } = useLanguage();
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  useEffect(() => { api.get("/auth/me/lf-alerts").then((r) => { setCfg(r.data); setSaved(r.data); }).catch(() => {}); }, []);

  const save = async (next = cfg) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me/lf-alerts", { enabled: next.enabled, city: next.city, province: next.province, radius_km: next.radius_km, frequency: next.frequency || "instant" });
      setCfg(data);
      setSaved(data);
      toast.success(t("lfa_saved"));
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };

  const toggle = (v) => {
    const next = v ? { ...cfg, enabled: true } : { ...saved, enabled: false };
    setCfg(next);
    if (!v || next.city) save(next);
  };

  if (!cfg) return null;
  return (
    <div className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6" data-testid="lf-alert-prefs">
      <div className="flex items-start gap-4">
        <span className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><BellRing size={20} className="text-red-600" /></span>
        <div className="flex-1">
          <p className="font-display font-bold text-[#12234A]">{t("lfa_title")}</p>
          <p className="text-sm text-slate-500 mt-0.5">{t("lfa_desc")}</p>
        </div>
        <Switch checked={cfg.enabled} disabled={saving} onCheckedChange={toggle} data-testid="lf-alert-toggle" />
      </div>
      {cfg.enabled && (
        <div className="mt-5 space-y-3" data-testid="lf-alert-area">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("lfa_city")}</Label>
              <Input value={cfg.city} onChange={(e) => set("city", e.target.value)} data-testid="lf-alert-city" placeholder="Toronto" className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <Label>{t("lfa_province")}</Label>
              <Select value={cfg.province} onValueChange={(v) => set("province", v)}>
                <SelectTrigger data-testid="lf-alert-province" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {PROVINCES.filter((p) => p.code !== "all").map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("lfa_radius")}</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="lf-alert-radius">
              {RADII.map((r) => (
                <button key={r} type="button" onClick={() => set("radius_km", r)} data-testid={`lf-alert-radius-${r}`}
                  className={`h-9 px-3 rounded-lg text-sm font-semibold border transition-colors ${cfg.radius_km === r ? "bg-[#12234A] text-white border-[#12234A]" : "border-slate-200 text-slate-600 hover:border-[#F5551E]"}`}>
                  {r === 0 ? t("ny_province") : `${r} km`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("lfa_freq")}</Label>
            <div className="mt-1 flex flex-wrap gap-2" data-testid="lf-alert-frequency">
              {[["instant", t("lfa_instant")], ["daily", t("lfa_daily")]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("frequency", v)} data-testid={`lf-alert-freq-${v}`}
                  className={`h-9 px-3 rounded-lg text-sm font-semibold border transition-colors ${(cfg.frequency || "instant") === v ? "bg-[#12234A] text-white border-[#12234A]" : "border-slate-200 text-slate-600 hover:border-[#F5551E]"}`}>{l}</button>
              ))}
            </div>
            {cfg.frequency === "daily" && <p className="text-xs text-slate-500 mt-1.5" data-testid="lf-alert-daily-hint">{t("lfa_daily_hint")}</p>}
          </div>
          {cfg.city && cfg.known_city === false && <p className="text-xs text-amber-600" data-testid="lf-alert-approx">{t("lfa_approx")}</p>}
          <Button onClick={() => save()} disabled={saving} data-testid="lf-alert-save" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
            {saving ? <Loader2 size={16} className="animate-spin" /> : t("lfa_save")}
          </Button>
        </div>
      )}
    </div>
  );
}
